import { firestore } from "firebase/app";
import { formatDateCompact } from "@/utils/DateFormatter";

export enum ReportUsage {
  INTERNAL = "INTERNAL",
  EXTERNAL = "EXTERNAL", 
}

export enum ExtendedReportUsage {
  INTERNAL = "INTERNAL",
  EXTERNAL = "EXTERNAL",
  EXT_SHORT = "EXT_SHORT",
  EXT_IMAGES = "EXT_IMAGES",
  EXT_TIME_TRACKING = "EXT_TIME_TRACKING",
  SERVICE = "SERVICE",
  SERVICE_JSON = "SERVICE_JSON",
  MEASUREMENT = "MEASUREMENT",
  DOCUMENT = "DOCUMENT",
  HY_DRY_CERTIFICATE = "HY_DRY_CERTIFICATE",
  LO = "LO",
  SA = "SA",
  TA = "TA",
  WH = "WH",
  EXT_SINGLE_TIME_ENTRY = "EXT_SINGLE_TIME_ENTRY",
  /**
   * @deprecated
   * `SV_IMAGE` is deprecated and should not be used. Please use `SA`.
   */
  SV_IMAGE = "SV_IMAGE",
  OTHER = "OTHER",
}

export enum ReportType {
  DRYING = "DRYING_REPORT",
  INTERIM_REPORT = "INTERIM_REPORT",
  FINAL_REPORT = "FINAL_REPORT",
  DAMAGE_REPORT = "DAMAGE_REPORT",
  LEAK_DETECTION_REPORT = "LEAK_DETECTION_REPORT",
}

export enum ExtendedReportType {
  DRYING = "DRYING_REPORT",
  INTERIM_REPORT = "INTERIM_REPORT",
  FINAL_REPORT = "FINAL_REPORT",
  DAMAGE_REPORT = "DAMAGE_REPORT",
  LEAK_DETECTION_REPORT = "LEAK_DETECTION_REPORT",
  TIME_TRACKING = "TIME_TRACKING",
  OTHER = "OTHER",
}

export enum UploadStatus {
  FORWARDED = "FORWARDED",
  PENDING = "PENDING",
  NOT_NEEDED = "NOT_NEEDED",
}

export enum ReportsList {
  SUMMARIZED_REPORTS_ONLY = "SUMMARIZED_REPORTS_ONLY",
  INDIVIDUAL_PDF_DOCUMENTS = "INDIVIDUAL_PDF_DOCUMENTS",
  SEPARATE_PDF_AND_TIME_ENTRIES = "SEPARATE_PDF_AND_TIME_ENTRIES",
}

export enum DocumentsAsSeparateReport {
  NO = "NO",
  YES = "YES",
}

export enum SendReportsOption {
  LOCAL_EMAIL = "LOCAL_EMAIL",
  IRES_CLOUD = "IRES_CLOUD",
  NO = "NO",
}

export enum InterimReportPhase {
  INTERIM_DRYING = "INTERIM_DRYING",
  INTERIM_REPAIR = "INTERIM_REPAIR",
}

export default interface Report {
  id: string;
  fileSizeInBytes?: number;
  createdBy: string | null;
  projectName: string;
  projectNumber: string;
  projectId: string;
  reportDate: firestore.Timestamp;
  startDate: firestore.Timestamp;
  endDate?: firestore.Timestamp | null;
  wasUploaded: boolean;
  lastTimeSend?: firestore.Timestamp | null;
  sendBy?: string | null;
  projectFinished: boolean;
  outdated: boolean;
  storageLocation: string;
  jsonDataStorageLocation?: string;
  forwardStatus: UploadStatus;
  reportType: ReportType;
  usage: ReportUsage;
  extendedReportType?: ExtendedReportType;
  extendedReportUsage?: ExtendedReportUsage;
  fileName?: string;
  fileNameToDisplay?: string | null;
  fileNameToDownload?: string | null;
  interimReportPhase?: InterimReportPhase | null;
  /** Whether the associated phase was complete (closed) when this report was created. Null for legacy reports. */
  phaseComplete?: boolean | null;
}

export function reportStorageLocationToDownloadRef(storageLocation?: string) {
  if (!storageLocation) {
    return null
  }

  const tokens = storageLocation.split("/");

  return `${tokens[3]}/${tokens[4]}`;
}

export function getReportName(
  vue: Vue,
  reportDate: firestore.Timestamp,
  reportUsage: ReportUsage,
  reportType: ReportType
) {
  const date = formatDateCompact(reportDate);
  let reportUsageString = "";
  let reportTypeString = "";

  switch (reportUsage) {
    case ReportUsage.INTERNAL:
      reportUsageString = vue.$t("report.reportUsage.internal").toString();
      break;
    case ReportUsage.EXTERNAL:
      reportUsageString = vue.$t("report.reportUsage.external").toString();
      break;
  }

  switch (reportType) {
    case ReportType.FINAL_REPORT:
      reportTypeString = vue.$t("report.reportType.finalReport").toString();
      break;
    case ReportType.DAMAGE_REPORT:
      reportTypeString = vue.$t("report.reportType.damageReport").toString();
      break;
    case ReportType.LEAK_DETECTION_REPORT:
      reportTypeString = vue.$t("report.reportType.leakDetection").toString();
      break;
    case ReportType.INTERIM_REPORT:
      reportTypeString = vue.$t("report.reportType.interim").toString();
      break;
  }

  return reportType === ReportType.INTERIM_REPORT
    ? `${reportTypeString}_${date}`
    : `${reportTypeString}_${reportUsageString}_${date}`;
}

/**
 * Determines the report type label prefix based on phase completion status.
 * When `phaseComplete` is non-null, overrides the default report type label
 * with "Bericht {subject}" (complete) or "Zwischenbericht {subject}" (incomplete).
 * Excluded for final reports, time tracking, and document report usages.
 *
 * @param phaseComplete Whether the phase was complete when the report was created.
 * @param extendedReportType The extended report type.
 * @param extendedReportUsage The extended report usage.
 * @param interimReportPhase The interim report phase (drying or repair).
 * @param completedLabel Translated label for a completed-phase report (e.g. "Bericht").
 * @param interimLabel Translated label for an interim report (e.g. "Zwischenbericht").
 * @param subjects Translated subject strings keyed by report type.
 * @returns The overridden report type label, or null if no override applies.
 */
export function getPhaseAwareReportTypeLabel(
  phaseComplete: boolean | null | undefined,
  extendedReportType: ExtendedReportType | ReportType,
  extendedReportUsage: ExtendedReportUsage | ReportUsage,
  interimReportPhase: InterimReportPhase | null | undefined,
  completedLabel: string,
  interimLabel: string,
  subjects: {
    drying: string;
    repair: string;
    damageAssessment: string;
    leakDetection: string;
  },
): string | null {
  if (
    phaseComplete == null ||
    extendedReportType === ExtendedReportType.FINAL_REPORT ||
    extendedReportType === ExtendedReportType.TIME_TRACKING ||
    extendedReportUsage === ExtendedReportUsage.DOCUMENT
  ) {
    return null;
  }

  const prefix = phaseComplete ? completedLabel : interimLabel;

  let subject = "";
  switch (extendedReportType) {
    case ExtendedReportType.INTERIM_REPORT:
      subject = interimReportPhase === InterimReportPhase.INTERIM_REPAIR
        ? subjects.repair
        : subjects.drying;
      break;
    case ExtendedReportType.DRYING:
      subject = subjects.drying;
      break;
    case ExtendedReportType.DAMAGE_REPORT:
      subject = subjects.damageAssessment;
      break;
    case ExtendedReportType.LEAK_DETECTION_REPORT:
      subject = subjects.leakDetection;
      break;
  }

  return subject ? `${prefix} ${subject}` : prefix;
}
